import type {
	PortalInvoiceDetailResponseDto,
	PortalInvoiceListResponseDto,
	PortalInvoicePaymentOptionsResponseDto,
	PortalInvoiceQueryDto,
	PortalPayInvoiceDto,
	PortalPayInvoiceResponseDto,
} from '@supply-mover-2-0/shared';
import {
	buildQueryString,
	portalFetch,
	portalFetchBlob,
} from '../lib/apiClient';

/**
 * Fetch paginated invoice list with optional filters.
 */
export async function getInvoices(
	token: string,
	params: PortalInvoiceQueryDto,
): Promise<PortalInvoiceListResponseDto> {
	const qs = buildQueryString({
		page: params.page,
		limit: params.limit,
		search: params.search,
		clientId: params.clientId,
		dateFrom: params.dateFrom,
		dateTo: params.dateTo,
		paymentStatus: params.paymentStatus,
		type: params.type,
	});
	return portalFetch<PortalInvoiceListResponseDto>(`/invoices${qs}`, token);
}

/**
 * Fetch a single invoice detail by ID.
 */
export async function getInvoiceDetail(
	token: string,
	invoiceId: number,
): Promise<PortalInvoiceDetailResponseDto> {
	return portalFetch<PortalInvoiceDetailResponseDto>(
		`/invoices/${invoiceId}`,
		token,
	);
}

/**
 * Get payment options for an invoice — supported types and saved methods.
 */
export async function getInvoicePaymentOptions(
	token: string,
	invoiceId: number,
): Promise<PortalInvoicePaymentOptionsResponseDto> {
	return portalFetch<PortalInvoicePaymentOptionsResponseDto>(
		`/invoices/${invoiceId}/payment-options`,
		token,
	);
}

/**
 * Pay an invoice with immediate capture. Supports partial payments.
 */
export async function payInvoice(
	token: string,
	invoiceId: number,
	payload: PortalPayInvoiceDto,
): Promise<PortalPayInvoiceResponseDto> {
	return portalFetch<PortalPayInvoiceResponseDto>(
		`/invoices/${invoiceId}/pay`,
		token,
		{
			method: 'POST',
			body: JSON.stringify(payload),
		},
	);
}

/**
 * Download an invoice (or credit memo) as a PDF blob (PD-14606).
 */
export async function downloadInvoicePdf(
	token: string,
	invoiceId: number,
): Promise<Blob> {
	return portalFetchBlob(`/invoices/${invoiceId}/pdf`, token);
}
